import React, { useState, useEffect, useRef } from 'react';

interface ModelSelectorProps {
  models: string[];
  selectedModel: string;
  onModelSelect: (model: string) => void;
  disabled?: boolean;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({ models, selectedModel, onModelSelect, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleModelClick = (model: string) => {
    onModelSelect(model);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex flex-col gap-4 items-center relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`rounded-full text-base text-black font-medium border-transparent bg-clip-padding focus:outline-none flex items-center gap-2 justify-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={{
          background:
            'linear-gradient(white, white) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box',
          border: '2px solid transparent',
          borderWidth: '2px',
          borderRadius: '100px',
          minWidth: '149px',
          width: '152px',
          height: '42px',
          paddingTop: '9px',
          paddingRight: '27px',
          paddingBottom: '9px',
          paddingLeft: '27px',
          gap: '10px',
          opacity: 1,
          fontFamily: 'Maven Pro',
          outline: 'none',
        }}
      >
        {selectedModel}
      </button>
      
      {isOpen && (
        <div 
          className="bg-[#121212] absolute top-full mt-2 rounded-lg shadow-lg z-10 border-2 border-transparent bg-clip-padding"
          style={{
            background:
              'linear-gradient(white, white) padding-box, linear-gradient(#F39A81, #959BEA) border-box',
            border: '2px solid transparent',
            colorScheme: 'light',
            fontFamily: 'Maven Pro, sans-serif',
            minWidth: '149px',
            width: 'auto',
          }}
        >
          {models.map((model) => {
            const isDisabled = model === 'Hunyuan' || model === 'Mochi' || model === 'Opensora' || model === 'Wan 2.2';
            return (
              <button
                key={model}
                onClick={() => !isDisabled && handleModelClick(model)}
                disabled={isDisabled}
                className={`w-full px-4 py-2 text-left first:rounded-t-lg last:rounded-b-lg transition-colors ${
                  isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
                }`}
                style={{ 
                  outline: 'none',
                  background: selectedModel === model 
                    ? "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box"
                    : "#FFFFFF",
                  border: selectedModel === model ? "2px solid transparent" : "none",
                  whiteSpace: 'nowrap',
                  opacity: isDisabled ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (selectedModel !== model && !isDisabled) {
                    e.currentTarget.style.background = "#F9FAFB";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedModel !== model && !isDisabled) {
                    e.currentTarget.style.background = "#FFFFFF";
                  }
                }}
              >
                {model}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModelSelector;
